import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components';
import { greyColor, maxTablet, maxWidth, primaryColor, primaryFont, primaryHeadingColor, retina, wLight, wMedium } from '../../Utils/utilsStyle';
import { Popover, DatePicker } from 'antd';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { selectKPMGFlag, selectUserCreationDate } from '../../Selector/GlobalSearchContentSelector';
import { useDispatch, useSelector } from 'react-redux';
import { actions as serviceLineActions } from '../../Slice/ServiceLinesSlice';
import { Filter } from '../../Types/FilterTypes';
import { IsMobile, IsTablet, valueToObject } from '../../Utils/UtilityFunctions';
import { selectServicelineCompanyAdded, selectServicelineSelectedRegions } from '../../Selector/ServiceLinesSelector';
import {
  CalendarOutlined
} from '@ant-design/icons';
import { actions as flurryActions } from 'Slice/HelperSlice';

const { RangePicker } = DatePicker;
const Wrapper = styled.div`
  .date-filter-btn {
    min-width: 140px;
    border-radius: 8px;
    border: 0.711px solid #DADADA;
    background: #FFF;
    padding: 9px 16px;
    line-height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    &.active {
      background: rgb(222, 246, 230);
      .text {
        color: ${primaryColor};
      }
      .icon {
        svg {
          fill: ${primaryColor};
        }
      }
    }
    .text {
      color: ${greyColor};
      font-family: ${primaryFont};
      font-size: 16px;
      text-align: center;
      font-weight: ${wMedium};
      line-height: 20px;
      letter-spacing: -0.4px;
      @media all and (max-width: ${maxWidth}), ${retina} {
        font-size: 14px;
        line-height: 18px;
      }
    }
    .icon {
      svg {
        width: 20px;
        height: 20px;
        fill: ${greyColor};
      }
    }
    @media all and (max-width: ${maxWidth}), ${retina} {
      padding: 6px 16px;
      border-radius: 6px;
    }
    @media all and (max-width: ${maxTablet}) {
      min-width: 50px;
      padding: 2px 12px;
    }
  }
`;

type Props = {
  filter?: Filter[]
  customFilters?: Filter[]
  serviceLineId?: number
  widgetName?: string
  name?: string
}

const defaultProps = {
  serviceLineId: 0,
  widgetName: '',
  name: '',
};

const SLCustomDateFilter = function SLCustomDateFilter(props: Props) {
  const { filter, serviceLineId, widgetName, name, customFilters } = props;
  const dispatch = useDispatch();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const Ranges = [
    'Last 2 Days',
    'Last 7 Days',
    'Last 2 Weeks',
    'Last 1 Month',
    'Last 3 Months',
    'All Time',
    'Custom Range',
  ];
  type RangeType = typeof Ranges[number];

  const [open, setOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState<RangeType>("Last 3 Months");
  const [lastSelectedRange, setLastSelectedRange] = useState<RangeType>('Last 3 Months');
  const userCreationDate = useSelector(selectUserCreationDate);
  const [active, setActive] = useState<boolean>(false);
  const [customRange, setCustomRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const KPMGFlag = useSelector(selectKPMGFlag);

  const isMobile = IsMobile();
  const isTab = IsTablet();
  const selectedRegions = useSelector(selectServicelineSelectedRegions);
  const companyAdded = useSelector(selectServicelineCompanyAdded);
  const skipDuplicateApiCall = useRef(false);
  const [initalized, setInitialized] = useState(false);
  const host = window.location.href;

  useEffect(() => {
    if (companyAdded) {
      skipDuplicateApiCall.current = true;
      setSelectedRange('Last 3 Months');
      setLastSelectedRange('Last 3 Months');
      setCustomRange(null);
      setActive(false);
    }
  }, [companyAdded]);

  useEffect(() => {
    skipDuplicateApiCall.current = false;
  }, [serviceLineId]);

  const setRangeDates = (range: RangeType): { start: Dayjs; end: Dayjs; allTime?: boolean } | null => {
    const today = dayjs().endOf('day');

    switch (range) {
      case 'Last 2 Days':
        return {
          start: today.subtract(1, 'day').startOf('day'),
          end: today,
        };
      case 'Last 7 Days':
        return {
          start: today.subtract(6, 'day').startOf('day'),
          end: today,
        };
      case 'Last 2 Weeks':
        return {
          start: today.subtract(13, 'day').startOf('day'),
          end: today,
        };
      case 'Last 1 Month':
        return {
          start: today.subtract(29, 'day').startOf('day'),
          end: today,
        };
      case 'Last 3 Months':
        return {
          start: today.subtract(89, 'day').startOf('day'),
          end: today,
        };
      case 'All Time':
        return {
          start: dayjs(userCreationDate),
          end: today,
          allTime: true,
        };
      default:
        return null;
    }
  };

  const selectRangeHandler = (range: RangeType) => {
    setSelectedRange(range);
    setActive(true);
    if (range !== 'Custom Range') {
      setCustomRange(null);
      setLastSelectedRange(range);
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!userCreationDate) return;

    if (skipDuplicateApiCall.current) {
      skipDuplicateApiCall.current = false;
      return;
    }

    let start: Dayjs;
    let end: Dayjs;
    let allTime: boolean;

    if (selectedRange === 'Custom Range') {
      if (!customRange?.[0] || !customRange?.[1]) return;
      start = customRange[0].startOf('day');
      end = customRange[1].endOf('day');
      allTime = false;

    } else {
      const rangeDates = setRangeDates(selectedRange);
      if (!rangeDates) return;
      start = rangeDates.start;
      end = rangeDates.end;
      allTime = rangeDates.allTime ?? false;
    }

    const filterObject = {
      start_date: start.format('YYYY-MM-DD HH:mm:ss'),
      end_date: end.format('YYYY-MM-DD HH:mm:ss'),
      type: selectedRange,
      allTime,
    };

    dispatch(serviceLineActions.setFilters(filterObject));
    const subRegions = selectedRegions.join(",");

    if (!initalized) {
      dispatch(serviceLineActions.getServiceLineDetails({
        id: serviceLineId ?? 0,
        page: 1,
        dateRangeFilter: filterObject,
        ...(KPMGFlag && { kpmgMode: true }),
        widget_name: widgetName,
        regions: subRegions,
      }))
      setInitialized(true);
    } else {
      dispatch(serviceLineActions.getServiceLineDetails({
        id: serviceLineId ?? 0,
        page: 1,
        dateRangeFilter: filterObject,
        ...(KPMGFlag && { kpmgMode: true }),
        widget_name: widgetName,
        regions: subRegions,
        ...(customFilters && customFilters.length > 0 && { customFilters }),
        ...(filter && filter.length > 0 && { filters: filter }),
      }))
      dispatch(flurryActions.callFlurryEvent(
        valueToObject('ServiceLines_Filter_By_Date', 0, {
          start_date: filterObject.start_date,
          end_date: filterObject.end_date,
          host,
        }),
      ));
    }
  }, [selectedRange, customRange?.[0], userCreationDate]);

  const content = (
    <div className='filter-dropdown'>
      {Ranges.map((range) => (
        <div
          className={classNames('item', {
            'active': customRange?.[0] && customRange?.[1] ? range === selectedRange : range === lastSelectedRange
          })}
          key={range}
          onClick={() => selectRangeHandler(range)}
        >
          {range}
        </div>
      ))}

      {
        selectedRange === 'Custom Range' && (
          <div className='custom-range'>
            <RangePicker
              popupClassName='custom-range-popup'
              value={customRange}
              format='YYYY-MM-DD'
              disabledDate={(current) => current && (current.isAfter(dayjs(), 'day'))}
              onChange={(dates) => {
                if (!dates) return;
                setCustomRange(dates);
                setOpen(false);
              }}
              getPopupContainer={() => document.body}
            />
          </div>
        )
      }
    </div>
  )
  const displayLabel = !isMobile ? (customRange?.[0] && customRange?.[1] ? `${customRange[0].format('MMMM D, YYYY')} -> ${customRange[1].format('MMMM D, YYYY')}` : selectedRange === 'Custom Range' ? lastSelectedRange : selectedRange) : (customRange?.[0] && customRange?.[1] ? `${customRange[0].format('DD/MM/YY')} -> ${customRange[1].format('DD/MM/YY')}` : selectedRange === 'Custom Range' ? lastSelectedRange : selectedRange);

  return (
    <Wrapper ref={wrapperRef}>
      <Popover
        open={open}
        onOpenChange={(newOpen) => setOpen(newOpen)}
        trigger="click"
        placement='bottomLeft'
        // autoAdjustOverflow={false}
        getPopupContainer={() => document.body}
        content={content}
        zIndex={100000}
        classNames={{ root: "custom-date-popover" }}
      >
        <div
          role='button'
          aria-label='custom-date-filter'
          className={classNames('date-filter-btn', { active: active })}
        >
          {!isTab ? (
            <span className='text'>{displayLabel}</span>
          ) : (
            <span className='icon'><CalendarOutlined /></span>
          )}
        </div>
      </Popover>
    </Wrapper>
  );
};

SLCustomDateFilter.defaultProps = defaultProps;
export default SLCustomDateFilter;